import { MarkerClusterer } from '@googlemaps/markerclusterer';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { environment } from '@/config/environment';
import { getClientServiceLocations, searchClients } from '@/features/clients/api/clients';
import { loadGoogleMaps } from '@/features/maps/googleMaps';
import { getServiceLocation } from '@/features/service-locations/api/serviceLocations';

interface LocationMarker {
  id: number;
  clientName: string;
  locationName: string;
  latitude: number;
  longitude: number;
}

async function loadAllLocations(): Promise<LocationMarker[]> {
  const clients = await searchClients();
  const summaries = (
    await Promise.all(
      clients.map(async (client) =>
        (await getClientServiceLocations(client.id)).map((location) => ({ client, location })),
      ),
    )
  ).flat();
  const locations = await Promise.all(
    summaries.map(async ({ client, location }) => ({
      client,
      location: await getServiceLocation(client.id, location.id),
    })),
  );
  return locations
    .map(({ client, location }) => ({
      id: location.id,
      clientName: client.name,
      locationName: location.name,
      latitude: Number(location.address?.latitude),
      longitude: Number(location.address?.longitude),
    }))
    .filter(
      (location) => Number.isFinite(location.latitude) && Number.isFinite(location.longitude),
    );
}

export function MapPage() {
  const query = useQuery({ queryKey: ['all-service-locations-map'], queryFn: loadAllLocations });
  return (
    <Box sx={{ p: 1, height: 'calc(100vh - 58px)', display: 'flex', flexDirection: 'column' }}>
      <Paper elevation={0} sx={{ ...paperSx, mb: 1.5, px: 1, py: 0.5 }}>
        <Stack direction="row" alignItems="center" minHeight={44}>
          <Button
            sx={commandButtonSx}
            startIcon={<RefreshIcon />}
            onClick={() => void query.refetch()}
          >
            Refresh
          </Button>
        </Stack>
      </Paper>
      {query.isError && <Alert severity="error">Service locations could not be loaded.</Alert>}
      {query.isLoading ? (
        <Paper elevation={0} sx={{ ...paperSx, flex: 1, display: 'grid', placeItems: 'center' }}>
          <CircularProgress aria-label="Loading service location map" />
        </Paper>
      ) : (
        <AllLocationsMap locations={query.data ?? []} />
      )}
    </Box>
  );
}

function AllLocationsMap({ locations }: { locations: LocationMarker[] }) {
  const mapElement = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!environment.googleMapsApiKey || !mapElement.current || locations.length === 0) return;
    let active = true;
    let clusterer: MarkerClusterer | undefined;
    let markers: google.maps.Marker[] = [];
    void loadGoogleMaps(environment.googleMapsApiKey)
      .then((googleApi) => {
        if (!active || !mapElement.current) return;
        const map = new googleApi.maps.Map(mapElement.current, {
          center: { lat: 54.5, lng: -3.5 },
          zoom: 6,
          mapTypeId: googleApi.maps.MapTypeId.TERRAIN,
          mapTypeControl: true,
          streetViewControl: true,
          fullscreenControl: true,
        });
        const bounds = new googleApi.maps.LatLngBounds();
        const info = new googleApi.maps.InfoWindow();
        markers = locations.map((location) => {
          const position = { lat: location.latitude, lng: location.longitude };
          bounds.extend(position);
          const marker = new googleApi.maps.Marker({
            position,
            title: `${location.clientName} - ${location.locationName}`,
          });
          marker.addListener('click', () => {
            const content = document.createElement('div');
            content.style.fontWeight = '600';
            content.textContent = `${location.clientName} - ${location.locationName}`;
            info.setContent(content);
            info.open({ map, anchor: marker });
          });
          return marker;
        });
        clusterer = new MarkerClusterer({ map, markers });
        const uniquePositions = new Set(
          locations.map((location) => `${location.latitude},${location.longitude}`),
        ).size;
        if (uniquePositions === 1) {
          map.setCenter(bounds.getCenter());
          map.setZoom(12);
        } else map.fitBounds(bounds, 48);
      })
      .catch(() => {
        if (active) setError('Google Maps could not be loaded for this environment.');
      });
    return () => {
      active = false;
      clusterer?.clearMarkers();
      markers.forEach((marker) => marker.setMap(null));
    };
  }, [locations]);
  return (
    <Paper elevation={0} sx={{ ...paperSx, flex: 1, minHeight: 480, overflow: 'hidden' }}>
      {environment.googleMapsApiKey && locations.length ? (
        <>
          <Box
            ref={mapElement}
            role="img"
            aria-label="All service locations map"
            sx={{ width: '100%', height: '100%', minHeight: 480 }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </>
      ) : (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary">
            {environment.googleMapsApiKey
              ? 'No service locations with coordinates were found.'
              : 'Google Maps has not been configured for this environment.'}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}

const paperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
};
const commandButtonSx = {
  color: 'text.primary',
  '& .MuiButton-startIcon': { color: 'primary.main' },
};
